import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { addToCart, CartLine, setQty, toOrderBody } from '../lib/cart';
import { GuestSession, loadGuest, saveGuest } from '../lib/guest';
import { connectLive, LiveEvent, usePollWhileOffline } from '../lib/live';
import { navigate } from '../lib/router';
import type { Menu, MenuItem, Order } from '../lib/types';
import { Mango } from '../ui/icons';
import { GuestBill } from './GuestBill';
import { Tracker } from './Tracker';
import { Tray } from './Tray';

export interface GuestState {
  guest: GuestSession;
  /** Takeout from the customer link: waiting for the restaurant to accept. */
  pending: boolean;
  menu: Menu;
  items: Map<string, MenuItem>;
  soldOut: Set<string>;
  orders: Order[];
  cart: CartLine[];
  online: boolean;
  sending: boolean;
  error: string | null;
  add(itemId: string, choices: Record<string, string[]>, qty: number, note?: string): void;
  setQty(key: string, qty: number): void;
  send(): Promise<void>;
  clearError(): void;
}

/** Everything a seated guest sees: menu tray (/t) and order tracker (/t/orders), kept live over WebSocket. */
export function GuestApp({ path }: { path: string }) {
  const [guest] = useState(loadGuest);
  const [menu, setMenu] = useState<Menu | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [soldOut, setSoldOut] = useState<Set<string>>(new Set());
  const [cart, setCart] = useState<CartLine[]>([]);
  const [online, setOnline] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const token = guest?.token;
  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const [m, o, s] = await Promise.all([
        api<Menu>('GET', '/menu', { token }),
        api<{ orders: Order[] }>('GET', '/guest/orders', { token }),
        api<{ status: string; kind?: string; accepted?: boolean; paid?: boolean }>('GET', '/guest/session', { token }),
      ]);
      setMenu(m);
      setSoldOut(new Set(m.soldOut));
      setOrders(o.orders);
      if (s.status !== 'open') setEnded(true);
      setPending(s.kind === 'takeout' && s.accepted === false);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) setEnded(true);
      else setFatal(err.code === 'not_published' ? 'ร้านยังไม่เปิดเมนู · The menu is not ready yet.' : err.message);
    }
  }, [token]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!token) return;
    return connectLive(token, (e: LiveEvent) => {
      if (e.kind === 'order') {
        const o = e.order as Order;
        setOrders((prev) => (prev.some((x) => x.id === o.id) ? prev.map((x) => (x.id === o.id ? o : x)) : [...prev, o]));
      } else if (e.kind === 'soldOut') {
        setSoldOut((prev) => {
          const next = new Set(prev);
          if (e.available) next.delete(e.itemId as string); else next.add(e.itemId as string);
          return next;
        });
      } else if (e.kind === 'menu') {
        void api<Menu>('GET', '/menu', { token }).then((m) => { setMenu(m); setSoldOut(new Set(m.soldOut)); });
      } else if (e.kind === 'session') {
        const sess = e.session as { status: string; accepted?: boolean; kind?: string };
        if (sess.status !== 'open') setEnded(true);
        if (sess.kind === 'takeout') setPending(sess.accepted === false);
      }
    }, setOnline, () => void refresh());
  }, [token, refresh]);

  useEffect(() => { if (ended) saveGuest(null); }, [ended]);
  usePollWhileOffline(online, () => void refresh());

  const items = useMemo(() => new Map((menu?.categories ?? []).flatMap((c) => c.items).map((i) => [i.id, i])), [menu]);

  if (!guest) return <Message title="สแกน QR ที่โต๊ะ" text="Scan the QR code on your table to start ordering." />;
  if (ended) return <Message title="ขอบคุณค่ะ · Thank you!" text={guest.kind === 'takeout' ? `Order ${guest.tableName} is complete. Thank you for ordering from ${guest.restaurant}!` : `Table ${guest.tableName} is closed. Thank you for dining at ${guest.restaurant}!`} />;
  if (fatal) return <Message title="ขออภัย" text={fatal} />;
  if (!menu) return <main className="screen center"><div className="spinner" role="status" aria-label="Loading the menu" /></main>;

  const state: GuestState = {
    guest, pending, menu, items, soldOut, orders, cart, online, sending, error,
    add: (id, ch, q, note) => setCart((c) => addToCart(c, id, ch, q, note)),
    setQty: (key, q) => setCart((c) => setQty(c, key, q)),
    clearError: () => setError(null),
    async send() {
      if (!cart.length || sending) return;
      setSending(true);
      setError(null);
      try {
        const o = await api<Order>('POST', '/guest/orders', { token: guest.token, body: toOrderBody(cart) });
        setOrders((prev) => (prev.some((x) => x.id === o.id) ? prev : [...prev, o]));
        setCart([]);
        navigate('/t/orders');
      } catch (e) {
        const err = e as ApiError;
        if (err.code === 'session_closed') setEnded(true);
        else if (err.code === 'sold_out' || err.code === 'item_unavailable') {
          setError('บางเมนูเพิ่งหมด กรุณาตรวจสอบถาด · Something on your tray just sold out. Please check your tray.');
          void refresh();
        } else setError(err.message);
      } finally {
        setSending(false);
      }
    },
  };
  if (path.startsWith('/t/bill')) return <GuestBill s={state} />;
  return path.startsWith('/t/orders') ? <Tracker s={state} /> : <Tray s={state} />;
}

function Message({ title, text }: { title: string; text: string }) {
  return (
    <main className="screen center">
      <div className="logo" style={{ width: 64, height: 64 }}><Mango /></div>
      <h1 className="display" style={{ margin: 0, fontSize: 24 }}>{title}</h1>
      <p className="muted" style={{ margin: 0 }}>{text}</p>
    </main>
  );
}
